<template>
  <div v-if="error" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
    <span class="block sm:inline">{{ errorMessage }}</span>
  </div>

  <div class="flex flex-wrap -mx-3 mb-6">
    <div class="w-full md:w-1/2 px-3 mb-6 md:mb-0">
      <label class="block tracking-wide text-gray-800 text-sm mb-2" for="grid-firstName">
        First name
      </label>
      <input class="appearance-none block w-full text-gray-500 border border-gray-300 rounded py-3 px-4 leading-tight" id="grid-firstName" type="text" v-model="formData.field_firstName">
    </div>
    <div class="w-full md:w-1/2 px-3">
      <label class="block tracking-wide text-gray-800 text-sm mb-2" for="grid-lastName">
        Last name
      </label>
      <input class="appearance-none block w-full text-gray-500 border border-gray-300 rounded py-3 px-4 leading-tight" id="grid-lastName" type="text" v-model="formData.field_lastName">
    </div>
  </div>

  <div class="flex flex-wrap -mx-3 mb-6">
    <div class="w-full px-3">
      <label class="block tracking-wide text-gray-800 text-sm mb-2" for="grid-phone">
        Phone
      </label>
      <input class="appearance-none block w-full text-gray-500 border border-gray-300 rounded py-3 px-4 leading-tight" id="grid-phone" type="tel" v-model="formData.field_phone">
    </div>
  </div>

  <div class="flex flex-wrap -mx-3 mb-6">
    <!-- Street Address -->
    <div class="w-full px-3 mb-6">
      <label class="block tracking-wide text-gray-800 text-sm mb-2" for="grid-address">
        Street Address
      </label>
      <input class="appearance-none block w-full text-gray-500 border border-gray-300 rounded py-3 px-4 leading-tight" id="grid-address" type="text" v-model="formData.field_address">
    </div>

    <!-- Suburb -->
    <div class="w-full md:w-1/2 px-3 mb-6 md:mb-0">
      <label class="block tracking-wide text-gray-800 text-sm mb-2" for="grid-suburb">
        Suburb
      </label>
      <input class="appearance-none block w-full text-gray-500 border border-gray-300 rounded py-3 px-4 leading-tight" id="grid-suburb" type="text" v-model="formData.field_suburb">
    </div>

    <!-- State -->
    <div class="w-full md:w-1/4 px-3 mb-6 md:mb-0">
      <label class="block tracking-wide text-gray-800 text-sm mb-2" for="grid-state">
        State
      </label>
      <input class="appearance-none block w-full text-gray-500 border border-gray-300 rounded py-3 px-4 leading-tight" id="grid-state" type="text" v-model="formData.field_state">
    </div>

    <!-- Post Code -->
    <div class="w-full md:w-1/4 px-3">
      <label class="block tracking-wide text-gray-800 text-sm mb-2" for="grid-postCode">
        Post Code
      </label>
      <input class="appearance-none block w-full text-gray-500 border border-gray-300 rounded py-3 px-4 leading-tight" id="grid-postCode" type="text" v-model="formData.field_postCode">
    </div>
  </div>

  <div class="flex flex-wrap -mx-3 mb-6">
    <div class="w-full px-3">
      <label class="block tracking-wide text-gray-800 text-sm mb-2" for="grid-visaStatus">
        Visa status
      </label>

      <select class="appearance-none block w-full text-gray-500 border border-gray-300 rounded py-3 px-4 leading-tight" id="grid-visaStatus" v-model="formData.field_visaStatus">
        <option disabled value="">Select an option</option>
        <option value="Citizen">Citizen</option>
        <option value="Permanent Resident">Permanent Resident</option>
        <option value="Visa with full working rights">Visa with full working rights</option>
        <option value="Visa that requires sponsorship">Visa that requires sponsorship</option>
        <option value="Working Holiday Visa">Working Holiday Visa</option>
        <option value="I’m not a citizen and don’t hold a visa">I’m not a citizen and don’t hold a visa</option>
        <option value="Other">Other</option>
      </select>
    </div>
  </div>

  <div v-if="formData?.field_visaStatus == 'Other'" class="flex flex-wrap -mx-3 mb-6">
    <div class="w-full px-3">
      <label class="block tracking-wide text-gray-800 text-sm mb-2" for="grid-visa-other">
        Please specify your visa status
      </label>
      <input class="appearance-none block w-full text-gray-500 border border-gray-300 rounded py-3 px-4 leading-tight" id="grid-visa-other" type="text" v-model="formData.field_visaStatusOther">
    </div>
  </div>

  <div v-if="showIndustryRole" class="mb-10">
    <IndustryRoleFields :formData="formData" />
  </div>

  <div class="flex flex-wrap mb-10">
    <div class="w-full px-3">
      <div class="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
        <div>
          <p class="text-sm font-medium text-gray-800">Profile photo <span class="font-normal text-gray-400">(optional)</span></p>
          <p class="text-xs text-gray-500">Use a clear portrait photo. JPG or PNG up to 25&nbsp;MB.</p>
        </div>
        <button
          v-if="profilePicturePreview"
          type="button"
          class="text-sm font-medium text-rose-500 hover:text-rose-600"
          @click="removePhoto"
        >
          Remove photo
        </button>
      </div>

      <div class="mt-4 flex flex-col gap-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50/70 p-4 md:flex-row md:items-center">
        <div class="relative flex h-28 w-28 items-center justify-center overflow-hidden rounded-2xl bg-white shadow-inner">
          <img
            v-if="profilePicturePreview"
            :src="profilePicturePreview"
            alt="Profile preview"
            class="h-full w-full object-contain"
          />
          <div v-else class="flex flex-col items-center text-slate-400">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 15a3 3 0 10-.001-6.001A3 3 0 0012 15z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19.4 19.4a9 9 0 10-14.8 0" />
            </svg>
            <span class="mt-2 text-xs font-medium">800x800px</span>
          </div>
        </div>

        <div class="flex-1 space-y-3">
          <p class="text-sm text-slate-600">
            We'll automatically optimize large images so they load quickly across the marketplace.
          </p>
          <div class="flex flex-wrap gap-3">
            <label class="inline-flex cursor-pointer items-center justify-center rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200 transition hover:bg-slate-50">
              Upload photo
              <input type="file" accept="image/*" class="hidden" @change="handleProfilePictureUpload">
            </label>
            <button
              v-if="profilePicturePreview"
              type="button"
              class="text-sm font-medium text-brand hover:text-brand/80"
              @click="reCropPhoto"
            >
              Adjust crop
            </button>
          </div>
          <p class="text-xs text-slate-400">Every upload is compressed before it’s saved.</p>
          <p v-if="isCompressingPhoto" class="flex items-center gap-2 text-xs font-medium text-slate-500">
            <svg class="h-4 w-4 animate-spin text-brand" viewBox="0 0 24 24" fill="none">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8z"></path>
            </svg>
            Optimizing your photo…
          </p>
        </div>
      </div>
    </div>

    <ImageCropperModal
        v-if="showModal && profilePicturePreview"
        :image="profilePicturePreview"
        :show="showModal"
        @close="closeModal"
        @save="saveImage"
    />
  </div>


</template>

<script>
import IndustryRoleFields from './IndustryRoleFields.vue';
import ImageCropperModal from '@/components/modals/ImageCropperModal.vue';  // Import the standalone modal component
import imageCompression from 'browser-image-compression';

export default {
  components: {
    IndustryRoleFields,
    ImageCropperModal
  },
  props: {
    formData: {
      type: Object,
      required: true
    },
    showIndustryRole: {
      type: Boolean,
      default: true
    }
  },
  inject: ['toggleLoading'],
  data() {
    return {
      error: false,
      errorMessage: "",
      profilePicturePreview: null,  // Holds the image URL
      showModal: false,    // Controls whether the modal is shown
      profilePictureName: '',
      activePreviewUrl: null,
      isCompressingPhoto: false,
    }
  },
  methods: {
    logFileDebug(file, label) {
      if (!file) { return; }
      const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
      // eslint-disable-next-line no-console
      console.log(`[Profile Photo Debug] ${label}: name=${file.name || 'unnamed'}, size=${sizeMB}MB, type=${file.type}`);
    },
    // Handle closing the modal
    closeModal() {
      this.showModal = false;
    },
    // Save the cropped image
    saveImage(imageData) {
      if (!imageData) {
        this.closeModal();
        return;
      }
      this.formData.profilePicture = this.dataURLtoFile(
        imageData,
        (this.profilePictureName || 'profile-photo') + '.webp'
      );
      this.logFileDebug(this.formData.profilePicture, 'Cropped file');
      this.profilePicturePreview = imageData;
      this.showModal = false;
    },
    dataURLtoFile(dataUrl, filename) {
      const arr = dataUrl.split(',');
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new File([u8arr], filename, { type: mime });
    },
    removePhoto() {
      this.formData.profilePicture = null;
      this.profilePictureName = '';
      this.profilePicturePreview = null;
      if (this.activePreviewUrl) {
        URL.revokeObjectURL(this.activePreviewUrl);
        this.activePreviewUrl = null;
      }
    },
    reCropPhoto() {
      if (this.formData.profilePicture) {
        this.profilePicturePreview = URL.createObjectURL(this.formData.profilePicture);
        this.activePreviewUrl = this.profilePicturePreview;
        this.showModal = true;
        return;
      }

      if (this.profilePicturePreview) {
        this.showModal = true;
      }
    },
    showError(message) {
      this.error = true;
      this.errorMessage = message;

      // Clear previous timeout if it exists
      if (this.fadeTimeout) {
        clearTimeout(this.fadeTimeout);
      }

      // Hide the error after 5 seconds
      this.fadeTimeout = setTimeout(() => {
        this.error = false;
      }, 5000); // Error message stays visible for 5 seconds
    },
    initiateDefaultValues() {
      this.formData.field_visaStatus = this.formData.field_visaStatus !== undefined ? this.formData.field_visaStatus : '';
      if (this.showIndustryRole) {
        this.formData.selectedIndustry = this.formData.selectedIndustry !== undefined ? this.formData.selectedIndustry : '';
        this.formData.selectedRole = this.formData.selectedRole !== undefined ? this.formData.selectedRole : '';
      }
    },
    // Handle the profile picture upload
    async handleProfilePictureUpload(event) {
      const file = event.target.files[0];

      if (!file) {
        // Handle cancel event (when no file is selected)
        return; // Simply return if no file was selected
      }

      const MAX_UPLOAD_BYTES = 25 * 1024 * 1024; // 25 MB limit

      if (!file.type.startsWith('image/')) {
        this.showError('Please upload a valid image file.');
        event.target.value = '';
        return;
      }

      if (file.size > MAX_UPLOAD_BYTES) {
        this.showError('This file is larger than 25 MB. Please choose a smaller image.');
        event.target.value = '';
        return;
      }

      if (this.activePreviewUrl) {
        URL.revokeObjectURL(this.activePreviewUrl);
        this.activePreviewUrl = null;
      }

      this.profilePictureName = file.name;
      let imageToUse = file;
      this.logFileDebug(file, 'Original file');

      try {
        this.isCompressingPhoto = true;
        const options = {
          maxSizeMB: 1.25,
          maxWidthOrHeight: 1600,
          useWebWorker: true,
          initialQuality: 0.85
        };
        imageToUse = await imageCompression(file, options);
        this.logFileDebug(imageToUse, 'Optimized file');
      } catch (compressionError) {
        // eslint-disable-next-line no-console
        console.warn('Photo compression failed. Using original file.', compressionError);
      } finally {
        this.isCompressingPhoto = false;
      }

      this.formData.profilePicture = imageToUse;
      this.profilePicturePreview = URL.createObjectURL(imageToUse);
      this.activePreviewUrl = this.profilePicturePreview;
      this.showModal = true;
      event.target.value = '';
    },
    validate() {
      this.toggleLoading(true);
      // Reset error state
      this.error = false;
      this.errorMessage = '';

      let isValid = true;

      // Validate visa status
      if (!this.formData.field_visaStatus) {
        this.errorMessage = 'A valid visa status is required.';
        isValid = false;
      }

      // Validate "Other" visa status details
      if (this.formData.field_visaStatus === 'Other' && !this.formData.field_visaStatusOther) {
        this.errorMessage = 'Please specify your visa status details.';
        isValid = false;
      }

      if (!this.formData.field_address || !this.formData.field_suburb || !this.formData.field_state || !this.formData.field_postCode ||  this.formData.field_address.length < 5) {
        this.errorMessage = 'Please enter a valid address.';
        isValid = false;
      }

      if (this.showIndustryRole && !this.formData.selectedRole) {
        this.errorMessage = 'Please select a role from the list.';
        isValid = false;
      }

      if (this.showIndustryRole && !this.formData.selectedIndustry) {
        this.errorMessage = 'Please select an industry from the list.';
        isValid = false;
      }

      // Validate phone number
      if (!this.formData.field_phone) {
        this.errorMessage = 'A valid phone number is required.';
        isValid = false;
      }

      // Validate first and last name
      if (!this.formData.field_firstName || !this.formData.field_lastName) {
        this.errorMessage = 'Please provide both your first and last name.';
        isValid = false;
      }

      // If valid, emit the formData
      if (isValid) {
        this.$emit('submit-step2', this.formData); // Emit form data to parent
      } else {
        this.$root.notify(this.errorMessage, 'error');
      }

      this.toggleLoading(false);

      return isValid;
    }
  },
  watch: {
    'formData.field_visaStatus': function(newValue) {
      if (newValue !== 'Other') {
        this.formData.field_visaStatusOther = '';
      }
    }
  },
  mounted() {
    this.initiateDefaultValues();
    if (this.formData.profilePicturePreview) {
      this.profilePicturePreview = this.formData.profilePicturePreview;
    } else if (this.formData.croppedImage) {
      this.profilePicturePreview = this.formData.croppedImage;
    }
  },
  beforeUnmount() {
    if (this.activePreviewUrl) {
      URL.revokeObjectURL(this.activePreviewUrl);
    }
  }
};
</script>

<style scoped>
/* Fade transition for both fade-in and fade-out */
.fade-enter-active, .fade-leave-active {
  transition: opacity 1s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
/* You can adjust image styles as needed */
img {
  max-width: 100%; /* Ensures the image doesn't overflow the container */
  height: auto;   /* Maintains aspect ratio */
}
</style>
